import {
  FeatureFlag,
  FeatureFlags,
  FeatureFlagValue,
  ConfigFlags,
} from "../types.ts";
import { Experiences } from "../experiencesSchema.ts";
import { SearchPilotResult } from "../searchPilot/searchPilotSchema.ts";

export function buildFeatureFlags({
  defaultFlagValues,
  configFlags,
  parameterFlags,
  experiences,
  parameterPrefix,
  cookies,
  searchPilot,
}: {
  defaultFlagValues: Record<string, FeatureFlagValue>;
  configFlags?: ConfigFlags;
  parameterFlags?: Record<string, unknown>;
  experiences?: Experiences;
  parameterPrefix?: string;
  cookies?: Partial<Record<string, string>>;
  searchPilot?: SearchPilotResult;
}) {
  const defaultFlags = buildDefaultFlags(defaultFlagValues);
  return applyFeatureFlagOverrides({
    defaultFlags,
    experiences,
    parameterPrefix,
    cookies,
    parameterFlags,
    configFlags,
    searchPilot,
  });
}

function buildDefaultFlags(
  defaultFlagValues: Record<string, FeatureFlagValue>,
): FeatureFlags {
  const defaultFlags = {} as FeatureFlags;

  for (const [key, value] of Object.entries(defaultFlagValues)) {
    defaultFlags[key] = {
      source: "default",
      value,
    };
  }

  return defaultFlags;
}

function applyFeatureFlagOverrides({
  defaultFlags,
  experiences,
  parameterPrefix,
  cookies,
  parameterFlags,
  configFlags,
  searchPilot,
}: {
  defaultFlags: FeatureFlags;
  experiences?: Experiences;
  parameterPrefix?: string;
  cookies?: Partial<Record<string, string>>;
  parameterFlags?: Record<string, unknown>;
  configFlags?: ConfigFlags;
  searchPilot?: SearchPilotResult;
}): FeatureFlags {
  const experienceOverrides = getExperienceOverrides(
    experiences,
    parameterPrefix,
  );

  const cookieOverrides = getCookieOverrides(
    cookies?.["FEATURE_FLAG_OVERRIDES"],
  );

  const searchPilotOverrides = getSearchPilotOverrides(searchPilot);

  const parameterOverrides = parameterFlags || {};

  const configOverrides = configFlags || {};

  const flags = {} as FeatureFlags;

  for (const [key, defaultFlag] of Object.entries(defaultFlags)) {
    switch (true) {
      // Order of precedence for feature flag overrides
      // 1. Cookie
      // 2. Monetate Experience
      // 3. Parameter (Product List HTML Attribute)
      // 4. SearchPilot
      // 5. Config File
      case key in cookieOverrides:
        flags[key] = cookieOverrides[key]!;
        break;
      case key in experienceOverrides:
        flags[key] = experienceOverrides[key]!;
        break;
      case key in parameterOverrides:
        flags[key] = {
          source: "parameter",
          value: getParameterValue(parameterOverrides[key]),
        };
        break;
      case key in searchPilotOverrides:
        flags[key] = searchPilotOverrides[key]!;
        break;
      case key in configOverrides:
        flags[key] = {
          source: "config",
          value: getParameterValue(configOverrides[key]),
        };
        break;
      default:
        flags[key] = defaultFlag;
        break;
    }
  }

  return flags;
}

export function applyPageConfigOverrides(
  flags: FeatureFlags,
  configFlags?: ConfigFlags,
) {
  if (!configFlags) return flags;

  const result = {} as FeatureFlags;

  for (const [key, flag] of Object.entries(flags)) {
    if (flag.source === "default" && key in configFlags) {
      result[key] = {
        source: "config",
        value: configFlags[key]!,
      };
    } else {
      result[key] = flag;
    }
  }

  return result;
}

function getExperienceOverrides(
  experiences?: Experiences,
  parameterPrefix: string = "",
) {
  if (!experiences || experiences.length === 0) return {};

  return experiences.reduce(
    (
      acc: Record<string, Extract<FeatureFlag, { source: "monetate" }>>,
      experience,
    ) => {
      Object.entries(experience.parameters || {})
        .filter(([parameterName]) => parameterName.startsWith(parameterPrefix))
        .forEach(([parameterName, parameterValue]) => {
          const flagName = parameterName.replace(parameterPrefix, "");
          if (flagName in acc) {
            // TODO
            // recordExperienceParameterNameCollision();
            // eslint-disable-next-line no-console
            console.error(
              `Duplicate monetate experience parameter found. Parameter name: "${parameterName}". ExperienceIds: ${experience.experienceId}, ${acc[flagName]?.experienceId}`,
            );
          } else {
            acc = {
              ...acc,
              [flagName]: {
                source: "monetate",
                value: getParameterValue(parameterValue),
                experienceName: experience.experienceName,
                experienceId: Number(experience.experienceId),
                impressionId: experience.impressionId ?? null,
                variantLabel: experience.variantLabel,
              },
            };
          }
        });
      return acc;
    },
    {},
  );
}

function getCookieOverrides(featureFlagOverridesCookie?: string) {
  if (!featureFlagOverridesCookie) return {};

  try {
    const parsed: unknown = JSON.parse(featureFlagOverridesCookie);
    const result = {} as Record<
      string,
      Extract<FeatureFlag, { source: "cookie" }>
    >;

    if (parsed && typeof parsed === "object") {
      for (const [key, value] of Object.entries(parsed)) {
        result[key] = {
          source: "cookie",
          value: getParameterValue(value),
        };
      }
    }

    return result;
  } catch {
    return {};
  }
}

function getSearchPilotOverrides(
  searchPilot: SearchPilotResult | undefined,
): FeatureFlags {
  if (!searchPilot) return {};
  return Object.entries(searchPilot || {}).reduce(
    (acc, [flagName, value]) => {
      if (flagName === "variant") return acc;
      acc[flagName] = {
        source: "searchPilot",
        value: getParameterValue(value),
      };
      return acc;
    },
    {} as Record<string, Extract<FeatureFlag, { source: "searchPilot" }>>,
  );
}

const getParameterValue = (value: unknown): FeatureFlagValue => {
  if (["on", "true"].includes(String(value))) {
    return true;
  }
  if (["off", "false"].includes(String(value))) {
    return false;
  }
  return String(value);
};
